import { useMemo, useRef, useCallback } from 'react';
// quill@1.3 本身沒有型別宣告，型別來自 react-quill 相依的 @types/quill；
// 根目錄 .npmrc 設 node-linker=hoisted 後它會平鋪到 node_modules/@types 被自動採用，
// 原本壓在這裡的 @ts-expect-error 反而變成多餘而讓 tsc 報 TS2578。
import QuillModule from 'quill';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { richTextEditor as rte } from './RichTextEditor.style';

/**
 * Microsoft Word 預設「字型大小」選單（8–72 pt），依 96dpi 換算為 px（px = pt × 96/72）。
 * Quill 以 inline `font-size` 寫入 HTML。
 */
const WORD_FONT_SIZES_PX = [
  '11px', // 8pt
  '12px', // 9pt
  '13px', // 10pt
  '15px', // 11pt
  '16px', // 12pt
  '19px', // 14pt
  '21px', // 16pt
  '24px', // 18pt
  '27px', // 20pt
  '29px', // 22pt
  '32px', // 24pt
  '35px', // 26pt
  '37px', // 28pt
  '48px', // 36pt
  '64px', // 48pt
  '96px', // 72pt
] as const;

type TQuillWithAttributors = {
  import: (path: string) => { whitelist: string[] };
  register: (module: unknown, suppressWarning?: boolean) => void;
};

const Quill = QuillModule as unknown as TQuillWithAttributors;

const QuillSizeStyle = Quill.import('attributors/style/size');
QuillSizeStyle.whitelist = [...WORD_FONT_SIZES_PX];
Quill.register(QuillSizeStyle, true);

const SIZE_TOOLBAR_OPTIONS: ReadonlyArray<string | boolean> = [
  false,
  ...WORD_FONT_SIZES_PX,
];

type TProps = {
  value: string;
  onChange: (html: string) => void;
  /** 編輯區最小高度（px）；未指定則使用較大的預設值以利長文編輯 */
  editorMinHeightPx?: number;
};

export const RichTextEditor = ({
  value,
  onChange,
  editorMinHeightPx = 400,
}: TProps) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        const index = range ? range.index : Math.max(0, quill.getLength() - 1);
        quill.insertEmbed(index, 'image', reader.result as string);
        quill.setSelection({ index: index + 1, length: 0 });
      };
      reader.readAsDataURL(file);
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }, { size: SIZE_TOOLBAR_OPTIONS }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image', 'video'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [imageHandler],
  );

  const formats = [
    'header',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'link',
    'image',
    'video',
  ];

  return (
    <rte.Root editorMinHeightPx={editorMinHeightPx}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
      />
    </rte.Root>
  );
};
